import React from 'react';
import { useMutation } from '@apollo/client';
import { Card, Input } from '@avila-tek/ui/src';
import { Button } from '@avila-tek/ui/src/Button';
import { useRouter } from 'next/router';
import { useNotify, useUser } from '../../hooks';
import { Company, Goal } from '../../models';
import { UPDATE_COMPANY_DETAILS } from '../../graphql/mutation';
import { FormHeader } from '../shared/Forms';
import { validateGoalForm } from '../../lib/formValidations';

interface GoalFormProps {
  company: Company;
  goal?: Goal;
}

function GoalForm({ company, goal }: GoalFormProps) {
  const notify = useNotify();
  const router = useRouter();
  const [user] = useUser();
  const [disabled, setDisabled] = React.useState(false);
  const [formState, setFormState] = React.useState<Partial<Goal>>(
    goal ?? {
      metric: '',
      unit: '',
      periods: [],
    }
  );

  const [updateCompanyDetailsMutation] = useMutation<{
    updateCompanyDetails: Company;
  }>(UPDATE_COMPANY_DETAILS);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (disabled) return;

    setDisabled(true);
    try {
      // validate goal
      const error = validateGoalForm({ ...formState });
      if (error) return notify(error, 'error');
      if (goal) {
        updateGoal(formState as Goal);
      } else {
        createGoal(formState as Goal);
      }
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      setDisabled(false);
    }
  };

  const createGoal = async (goalState: Goal) => {
    try {
      const { data } = await updateCompanyDetailsMutation({
        variables: {
          data: {
            companyId: (user.company as Company)?._id,
            companyDetails: {
              ...company.companyDetails,
              _id: undefined,
              goals: [
                ...company.companyDetails.goals,
                {
                  ...goalState,
                },
              ],
            },
          },
        },
      });
      if (data?.updateCompanyDetails?._id) {
        notify('Meta creada', 'success');
        const index = data.updateCompanyDetails.companyDetails.goals.length - 1;
        const goalId =
          data.updateCompanyDetails.companyDetails.goals[index]._id;
        router.push(`/app/periods/create?goalId=${goalId}`);
      }
    } catch (error) {
      console.log(error);
      notify(error.message, 'error');
    }
  };

  const updateGoal = async (goalState: Goal) => {
    const newGoals = company.companyDetails.goals.map((_goal) => {
      if (_goal?._id === goal?._id) {
        return {
          ...goalState,
        };
      }
      return _goal;
    });

    try {
      const { data } = await updateCompanyDetailsMutation({
        variables: {
          data: {
            companyId: (user.company as Company)?._id,
            companyDetails: {
              ...company.companyDetails,
              _id: undefined,
              goals: newGoals,
            },
          },
        },
      });
      if (data?.updateCompanyDetails?._id) {
        notify('Meta actualizada', 'success');
      }
    } catch (error) {
      console.log(error);
      notify(error.message, 'error');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  return (
    <form
      method="POST"
      onSubmit={onSubmit}
      className="flex flex-col w-full space-y-10"
    >
      <Card>
        <FormHeader backBtnText="" title="Metas para beneficiarios" />
        <Card.Body>
          <div className="w-full flex flex-col gap-5">
            <div className="w-full flex gap-8 flex-col md:flex-row">
              <div className="w-full">
                <Input
                  name="metric"
                  value={formState?.metric ?? ''}
                  label="Nombre de la métrica"
                  type="text"
                  placeholder="Ingrese un nombre"
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="w-full">
                <Input
                  name="unit"
                  value={formState?.unit ?? ''}
                  label="Unidad"
                  type="text"
                  placeholder="Ingrese una unidad"
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
      <div className="flex md:w-1/3 md:ml-auto">
        <Button type="submit">Guardar</Button>
      </div>
    </form>
  );
}

export default GoalForm;
